<template>
  <div
    :class="{
      'post-preview-section': true,
      'post-preview-section--disabled': isSavingScheduleDate,
    }"
  >
    <div class="post-preview-section__post-snippet-wrap">
      <div class="post-preview-section__top-meta">
        <div v-if="postTypeTag" class="post-preview-section__post-type-tag">
          <IconEmbedded v-if="postTypeTagIconName" class="post-preview-section__post-type-tag-icon" :name="postTypeTagIconName" :size="13" color="rgba(0, 0, 0, 0.5)" />
          <span class="post-preview-section__post-type-tag-text">{{ postTypeTag }}</span>
        </div>
        <SocialPostNetworksRow
          v-if="!hasPostToAccounts"
          class="post-preview-section__social-logos-row post-preview-section__social-logos-row--top"
          :post="props.post"
          :allCustomerSocialChannels="props.customerSocialChannels"
          :isUnusedPost="props.isUnusedPost"
          :isEditable="!props.onlyAtlasReadonlyFieldsVisible"
          :previewNetwork="selectedPreviewNetwork"
          :isPreviewNetworkSelectable="true"
          @selectPreviewNetwork="selectedPreviewNetwork = $event"
          @updatePostToAccounts="selectedSocialChannels => emit('updatePostToAccounts', selectedSocialChannels)"
          @addSocialAccount="emit('addSocialAccount')"
        />
        <SocialPostNetworksPreviewRow
          v-else
          class="post-preview-section__social-logos-row post-preview-section__social-logos-row--top"
          :socialChannels="postAccountChannelsForPreview"
          :selectedChannel="selectedPreviewNetwork"
          :isUnusedPost="props.isUnusedPost"
          :tooltip-disabled="props.onlyAtlasReadonlyFieldsVisible"
          @toggleSelectedChannel="toggleSelectedPreviewNetwork"
        />
      </div>

      <!-- Status pill -->
      <SocialPostStatusPill
        v-if="props.isStatusAndSocialsPanelVisible && !props.onlyAtlasReadonlyFieldsVisible && props.post.status"
        class="post-preview-section__status-stripe"
        :color="getStatusColor"
        :isWithCheckIcon="props.post.status === SocialPostStatus.Approved"
        :text="getStatusText()"
      />

      <div class="post-preview-section__post-snippet-transition-shell" :style="previewTransitionShellStyle">
        <Transition name="post-preview-section-snippet-switch" mode="out-in" @before-leave="handlePreviewBeforeLeave" @enter="handlePreviewEnter">
          <div :key="previewLayoutTarget" class="post-preview-section__post-snippet-transition-item">
            <component
              :is="postPreviewComponent"
              class="post-preview-section__post-snippet"
              :style="props.isUnusedPost ? unusedPostStyle : undefined"
              :images-list="effectiveAssetIds"
              :lightboxKey="effectiveLightboxKey"
              v-bind="!isInstagramPreview ? { debugData: props.debugData, postType: effectivePostType } : {}"
            >
              <template #textarea>
                <ClampedText style="display: block; white-space: pre-wrap; font: 14px/18px sans-serif" :visibleLines="15">
                  {{ effectiveCaption }}
                </ClampedText>
              </template>
            </component>
          </div>
        </Transition>
      </div>

      <div v-if="slots.footer" style="padding: 10px 12px 0">
        <div class="post-preview-section__footer-divider"></div>
        <slot name="footer" />

        <div v-if="props.isPostScheduleVisible">
          <NoteWithIcon v-if="isScheduleWarningVisible" size="tiny" color="red" style="margin-bottom: 10px">
            <template #text
              ><b>Must be scheduled at least {{ POST_APPROVAL_MINUTES_AHEAD }} minutes in the future</b></template
            >
          </NoteWithIcon>

          <!-- Post schedule field -->
          <div v-if="!props.post.postedDetails?.length" class="post-schedule-field post-preview-section__post-schedule-field">
            <VueDatePicker
              class="post-schedule-field__invisible-date-field-overlay"
              ref="domRefPostScheduleInput"
              v-model="postScheduleDateTime"
              :minDate="postScheduleMinDate"
              :maxDate="postScheduleMaxDate"
              teleport="body"
              :is24="false"
              timePickerInline
              :config="{ setDateOnMenuClose: true, keepActionRow: true }"
              @update:modelValue="async () => await updatePostScheduleDate()"
            />

            <template v-if="props.post.scheduledToPostAt?.localDateTime">
              <span style="white-space: nowrap">
                {{ formatScheduledDate(props.post.scheduledToPostAt.localDateTime) }} {{ getTimeZoneAbbreviation() }}
                <LinkWithIcon color="gray" isDottedUnderline style="margin-left: -1px" @click="() => domRefPostScheduleInput.openMenu()">
                  <template #icon><IconEmbedded name="pencil_2" :size="16" /></template>
                </LinkWithIcon>
              </span>
            </template>

            <template v-else>
              <LinkWithIcon isDottedUnderline @click="() => domRefPostScheduleInput.openMenu()">
                <template #icon><IconEmbedded name="calendar_2" :size="18" /></template>
                <span>Schedule post</span>
              </LinkWithIcon>
            </template>
          </div>
          <!-- / Post schedule field -->

          <!-- "Posted on" field -->
          <div v-else-if="props.post.postedDetails?.length && props.post.scheduledToPostAt" class="posted-on-field post-preview-section__posted-on-field">
            <div class="posted-on-field__icons-row">
              <LinkWithIcon
                class="posted-on-field__icon"
                v-for="postedItemDetails in props.post.postedDetails.filter(postedItemDetails => postedItemDetails.postUrl)"
                :key="postedItemDetails.socialChannel"
                tag="a"
                color="blue"
                target="_blank"
                :href="postedItemDetails.postUrl"
              >
                <template #icon><IconEmbedded :name="postedItemDetails.socialChannel" :size="20" /></template>
              </LinkWithIcon>
            </div>
            <div class="posted-on-field__text">{{ `Posted ${formatScheduledDate(props.post.scheduledToPostAt.localDateTime)}` }} {{ getTimeZoneAbbreviation() }}</div>
          </div>
          <!-- / "Posted on" field -->
        </div>
      </div>

      <div v-if="props.showDebug" style="margin-top: 12px">
        <EvalResults v-if="props.showEvals" :evalResults="props.debugData?.evalResults" />
        <div style="display: flex; align-items: center; gap: 8px; margin-top: 5px">
          <button v-if="props.post" class="ui mini button" @click.prevent="historyModalRef?.openModal()">History</button>
          <template v-if="props.debugData">
            <button class="ui mini button" @click.prevent="debugModalRef?.openModal()">Debug</button>
            <LinkWithIcon v-if="props.showEvals && props.debugData?.evalResults" isDottedUnderline @click="reRunEvals">
              <template #icon><IconEmbedded name="zap_2" :size="18" /></template>
              <span>Redo Evals</span>
            </LinkWithIcon>
            <IconEmbedded v-if="showEvalsRequestedIndicator" name="check-circle_2-5" :size="18" color="rgba(0, 200, 0, 1)" style="transition: opacity 0.3s" />
          </template>
        </div>
        <div v-if="props.post?.id" class="post-preview-section__debug-post-id-row">
          <span v-if="postCreatedDateLabel" class="post-preview-section__debug-created">Created: {{ postCreatedDateLabel }}</span>
          <CopyTextNew class="post-preview-section__debug-post-id-copy">{{ props.post.id }}</CopyTextNew>
        </div>
        <div class="post-preview-section__debug-meta">
          <span v-if="creationFlowLabel">{{ creationFlowLabel }}:</span>
          <span> {{ props.post?.editCount ?? "unset" }} {{ props.post?.editCount === 1 ? "edit" : "edits" }}, {{ props.post?.captionStructure }} </span>
        </div>
      </div>
    </div>
    <!--<TwPostPreview class="post-preview-section__snippet" />-->

    <!-- Debug Modal -->
    <ImageSelectionDebugModal ref="debugModalRef" :debugData="props.debugData" />
    <!-- Event History Modal -->
    <PostEventHistoryModal v-if="props.post" ref="historyModalRef" :customerId="props.post.customerId" :postId="props.post.id" />
  </div>
</template>
<script setup lang="ts">
import { ref, onMounted, onUnmounted, useSlots, computed, nextTick } from "vue";
import ShareStopSummaryRepo from "@repos/ShareStopSummaryRepo";
import DayjsExtended from "@logic/DayjsExtended";
import { getPostScheduleMaxDate, getPostScheduleMinDate, isScheduledDateTooSoon, POST_APPROVAL_MINUTES_AHEAD, MS_TO_SHOW_WARNING } from "@logic/ContentCalendarValidator";
import { getAssetIdsForTarget, getCaptionForTarget } from "@logic/SocialPostChannelOverrides";

// Components
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import FacebookPostPreview from "./FacebookPostPreview.vue";
import InstagramPostPreview from "./InstagramPostPreview.vue";
import ClampedText from "@components/ui/ClampedText.vue";
import ImageSelectionDebugModal from "@components/ImageSelectAIDebug/ImageSelectionDebugModal.vue";
import PostEventHistoryModal from "@components/SocialPostPreview/PostEventHistoryModal.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import VueDatePicker from "@vuepic/vue-datepicker";
import EvalResults from "@components/SocialPostPreview/EvalResults.vue";
import CopyTextNew from "@components/CopyTextNew.vue";
import NoteWithIcon from "@components/NoteWithIcon.vue";
import SocialPostNetworksRow from "@components/SocialPostPreview/SocialPostNetworksRow.vue";
import SocialPostNetworksPreviewRow from "@components/SocialPostPreview/SocialPostNetworksPreviewRow.vue";
import SocialPostStatusPill from "@components/SocialPostPreview/SocialPostStatusPill.vue";

// Types
import { ImageSelectAIDebugData, SocialMediaPostData, SocialPostPublishType, SocialPostStatus, UpdatePostScheduledAtResponse } from "@contracts/shareStop";
import { CalendarItemCreationSource } from "@contracts/contentCalendar";
import { IconName } from "@contracts/IconName";
import { SocialChannels } from "@contracts/socialNetworks";
import type { SocialChannelData } from "@contracts/socialMediaSettings";

// DOM refs ===================================================================
const domRefPostScheduleInput = ref<any>(null);

const slots = useSlots();

defineOptions({ name: "SocialPostPreview" });

const props = withDefaults(
  defineProps<{
    showDebug?: boolean;
    showEvals?: boolean;
    readonlyMode?: boolean;
    debugData?: ImageSelectAIDebugData | null;
    post?: SocialMediaPostData | null;
    creationFlow?: CalendarItemCreationSource | null;
    isStatusAndSocialsPanelVisible?: boolean;
    isPostScheduleVisible?: boolean;
    customerSocialChannels?: Array<SocialChannelData>;
    onlyAtlasReadonlyFieldsVisible?: boolean; // Used by the dashboard
    isUnusedPost?: boolean; // When true, dims certain sections and shows "Unused" status
  }>(),
  {
    showDebug: false,
    showEvals: false,
    debugData: null,
    readonlyMode: false,
    post: null,
    creationFlow: null,
    isStatusAndSocialsPanelVisible: false,
    isPostScheduleVisible: false,
    customerSocialChannels: () => [],
    onlyAtlasReadonlyFieldsVisible: false,
    isUnusedPost: false,
  }
);

const emit = defineEmits<{
  (e: "updatePostSchedule", response: UpdatePostScheduledAtResponse): void;
  (e: "updatePostToAccounts", selectedSocialChannels: Array<SocialChannels>): void;
  (e: "addSocialAccount"): void;
}>();

// DOM refs ===================================================================
const debugModalRef = ref<InstanceType<typeof ImageSelectionDebugModal> | null>(null);
const historyModalRef = ref<InstanceType<typeof PostEventHistoryModal> | null>(null);

// Style applied to sections when post is unused (single source of truth for opacity)
const unusedPostStyle = { opacity: 0.5 };
const selectedPreviewNetwork = ref<SocialChannels | null>(null);
const hasPostToAccounts = computed<boolean>(() => (props.post?.postToAccounts?.length ?? 0) > 0);
const postAccountChannelsForPreview = computed<Array<SocialChannelData>>(() => (props.post?.postToAccounts ?? []).map(channelType => ({ channelType, channelId: channelType })));
const previewLayoutTarget = computed<SocialChannels>(() => selectedPreviewNetwork.value ?? "facebook");
const networkToShowDataFor = computed<SocialChannels>(() => selectedPreviewNetwork.value ?? props.post?.postToAccounts?.[0] ?? "facebook");
const isInstagramPreview = computed<boolean>(() => previewLayoutTarget.value === "instagram" || previewLayoutTarget.value === "tiktok");
const postPreviewComponent = computed(() => (isInstagramPreview.value ? InstagramPostPreview : FacebookPostPreview));
const effectivePostType = computed<SocialPostPublishType | null>(() => props.post?.postType ?? null);
const effectiveCaption = computed<string>(() => getCaptionForTarget(props.post, networkToShowDataFor.value));
const effectiveAssetIds = computed<Array<string>>(() => getAssetIdsForTarget(props.post, networkToShowDataFor.value));
const effectiveLightboxKey = computed<string>(() => props.post?.id ?? "post-prev");
// const isSocialsListEditable = computed<boolean>(() => props.post?.status !== SocialPostStatus.Published);
const postScheduleTimeZone = computed<string | null>(() => props.post?.scheduledToPostAt?.timeZone ?? null);
const postScheduleMinDate = computed<string>(() => getPostScheduleMinDate(postScheduleTimeZone.value));
const postScheduleMaxDate = computed<string>(() => getPostScheduleMaxDate(postScheduleTimeZone.value));
const postTypeTag = computed<string | null>(() => (effectivePostType.value && effectivePostType.value !== SocialPostPublishType.Post ? effectivePostType.value : null));
const postTypeTagIconName = computed(() => {
  if (effectivePostType.value === SocialPostPublishType.Reel) return "social-reel" as IconName;
  if (effectivePostType.value === SocialPostPublishType.Story) return "social-story" as IconName;
  if (effectivePostType.value === SocialPostPublishType.Post) return "social-post" as IconName; // hidden per logic above
  return null;
});
const postCreatedDateLabel = computed<string | null>(() => {
  if (!props.post?.createdDate) return null;
  const created = DayjsExtended(props.post.createdDate);
  return created.isValid() ? created.format("MMM D") : null;
});

const creationFlowLabel = computed<string | null>(() => {
  switch (props.creationFlow) {
    case "FullManual":
      return "Full manual";
    case "UserCreated":
      return "AI assisted";
    case "WeeklyGen":
      return "AI weekly run";
    case "CreatorVisitComplete":
      return "Creator Visit";
    default:
      return "Unknown source";
  }
});

const getStatusColor = computed(() => {
  if (props.post?.deletedDate) return "red";
  if (props.post?.status === SocialPostStatus.Approved || props.post?.status === SocialPostStatus.Published) return "green";
  return "gray";
});

function toggleSelectedPreviewNetwork(channel: SocialChannels): void {
  if (selectedPreviewNetwork.value === channel) {
    selectedPreviewNetwork.value = null;
    return;
  }
  selectedPreviewNetwork.value = channel;
}

// Update post ================================================================
const postScheduleDateTime = ref<string | null>(null);
const isSavingScheduleDate = ref<boolean>(false);
const isScheduleWarningVisible = ref<boolean>(false);

onMounted(() => {
  if (props.isPostScheduleVisible && props.post.scheduledToPostAt) {
    postScheduleDateTime.value = DayjsExtended(props.post.scheduledToPostAt.localDateTime).format("YYYY-MM-DDTHH:mm");
  }
});
async function updatePostScheduleDate() {
  // Validate the selected date is not too soon
  const localDateTime = postScheduleDateTime.value ? DayjsExtended(postScheduleDateTime.value).format("YYYY-MM-DDTHH:mm") : null;
  if (localDateTime && isScheduledDateTooSoon(localDateTime, postScheduleTimeZone.value)) {
    // Reset to original value (if possible)
    if (props.post.scheduledToPostAt) postScheduleDateTime.value = DayjsExtended(props.post.scheduledToPostAt.localDateTime).format("YYYY-MM-DDTHH:mm");

    // Show warning
    isScheduleWarningVisible.value = true;
    setTimeout(() => {
      isScheduleWarningVisible.value = false;
    }, MS_TO_SHOW_WARNING);

    return;
  }

  isSavingScheduleDate.value = true;

  // Passing in the local date time (without offset) to update the post schedule
  const updatedPost = await ShareStopSummaryRepo.updatePostScheduledAt(props.post, localDateTime);
  emit("updatePostSchedule", updatedPost);

  isSavingScheduleDate.value = false;
}

function formatScheduledDate(localDateTime: string): string {
  const dt = DayjsExtended(localDateTime);
  const format = dt.minute() === 0 ? "dddd [the] Do[,] ha" : "dddd [the] Do[,] h:mma";
  return dt.format(format);
}

function getTimeZoneAbbreviation(): string {
  try {
    if (!props.post?.scheduledToPostAt?.timeZone) return "";

    const timezoneCode = props.post.scheduledToPostAt.timeZone;
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezoneCode,
      timeZoneName: "short",
    }).formatToParts(new Date(props.post.scheduledToPostAt.localDateTime));
    return parts.find(p => p.type === "timeZoneName")?.value ?? "";
  } catch (error) {
    console.error("Error getting time zone abbreviation", error);
    return "ERR";
  }
}

function getStatusText(): string {
  const status = props.post?.status;
  if (!status) return "";
  if (props.post?.deletedDate) return "Deleted";
  const displayStatus = status === SocialPostStatus.Unscheduled ? SocialPostStatus.Draft : status;
  if (props.isUnusedPost && displayStatus === SocialPostStatus.Draft) {
    return "Unused";
  }
  return displayStatus;
}

// Preview switch smoothing ====================================================
// Facebook/Instagram previews have different intrinsic heights; this keeps the
// card from snapping so neighboring cards do not "jump" during network changes.
const PREVIEW_SWITCH_TRANSITION_MS = 220;
const previewShellHeightPx = ref<number | null>(null);
let previewHeightAnimationRaf: number | null = null;
let previewHeightResetTimeout: ReturnType<typeof setTimeout> | null = null;
const previewTransitionShellStyle = computed<Record<string, string> | undefined>(() => {
  if (previewShellHeightPx.value === null) return undefined;
  return { height: `${previewShellHeightPx.value}px` };
});

function isReducedMotionPreferred(): boolean {
  return typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function getPreviewBlockHeight(el: Element | null): number {
  if (!(el instanceof HTMLElement)) return 0;
  return Math.round(el.getBoundingClientRect().height);
}

function clearPreviewHeightAnimation(): void {
  if (previewHeightAnimationRaf !== null) {
    cancelAnimationFrame(previewHeightAnimationRaf);
    previewHeightAnimationRaf = null;
  }
  if (previewHeightResetTimeout) {
    clearTimeout(previewHeightResetTimeout);
    previewHeightResetTimeout = null;
  }
}

function handlePreviewBeforeLeave(el: Element): void {
  if (isReducedMotionPreferred()) return;
  const currentHeight = getPreviewBlockHeight(el);
  if (currentHeight > 0) previewShellHeightPx.value = currentHeight;
}

function handlePreviewEnter(el: Element): void {
  if (isReducedMotionPreferred()) {
    previewShellHeightPx.value = null;
    return;
  }

  clearPreviewHeightAnimation();
  nextTick(() => {
    previewHeightAnimationRaf = requestAnimationFrame(() => {
      previewHeightAnimationRaf = null;
      const nextHeight = getPreviewBlockHeight(el);
      if (nextHeight > 0) previewShellHeightPx.value = nextHeight;
      previewHeightResetTimeout = setTimeout(() => {
        previewShellHeightPx.value = null;
        previewHeightResetTimeout = null;
      }, PREVIEW_SWITCH_TRANSITION_MS + 40);
    });
  });
}

onUnmounted(() => {
  clearPreviewHeightAnimation();
});

// Re-run evals ===============================================================
const showEvalsRequestedIndicator = ref<boolean>(false);

async function reRunEvals() {
  if (!props.post?.id || !props.post?.customerId) return;
  await ShareStopSummaryRepo.rerunEvalsForPost(props.post.customerId, props.post.id);

  showEvalsRequestedIndicator.value = true;
  setTimeout(() => {
    showEvalsRequestedIndicator.value = false;
  }, 10000);
}
</script>

<style scoped lang="scss">
@import "@/scss/variables.scss";

// Used mark ==================================================================
.used-mark {
  align-items: center;

  &__header {
    display: flex;
    align-items: center;
    margin-top: 12px;
  }

  &__title {
    margin-right: 4px;
    font-weight: 500;
  }

  &__link {
    margin-right: 5px;
    position: relative;
    z-index: 2;
    font-weight: 500;
  }

  &__add-link-form {
    gap: 8px;
    margin-right: 5px;
    display: flex;
    position: relative;
    z-index: 2;
  }
}

// Post schedule field ========================================================
.post-schedule-field {
  @include standardFont;
  padding: 11px 0 0;
  border-top: 1px rgba(0, 0, 0, 0.15) solid;
  position: relative;
  z-index: 0;
  font-weight: 400;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;

  &__invisible-date-field-overlay {
    width: 100%;
    height: 100%;
    position: absolute;
    inset: 0 auto auto 0;
    z-index: 3;
    opacity: 0;
    pointer-events: none;
  }
}

// "Posted on" field ==========================================================
.posted-on-field {
  @include standardFont;
  display: flex;
  align-items: center;
  gap: 8px;

  &__text {
  }

  &__icons-row {
    padding: 0;
    margin: 0;
    display: flex;
    list-style: none;
  }

  &__icon {
  }
}

// Post preview section =======================================================
.post-preview-section {
  display: flex;
  flex-direction: column;

  &--disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  &__post-snippet-wrap {
    flex-grow: 1;
    padding: 20px 3px 13px;
    border: 1px #d7d7d7 solid;
    border-radius: 6px;
    display: flex;
    flex-direction: column;
    background: white;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.16);
    position: relative;
  }

  &__post-schedule-field {
    // margin: 0 0 11px;
  }

  &__posted-on-field {
  }

  &__social-logos-row {
    height: 24px; // helps keep the creative css below for __top-meta lined up properly
    margin-bottom: 0;

    &--top {
      display: inline-flex;
      align-items: center;
    }
  }

  &__top-meta {
    position: absolute;
    top: -15px;
    left: 12px;
    z-index: 31;
    display: inline-flex;
    align-items: center;
    padding: 2px 9px;
    border: 1px #d0d0d0 solid;
    border-bottom: none;
    border-radius: 12px 12px 0 0;
    background: white;
    gap: 6px;

    /* Keep chips/icons above the decorative seam mask below */
    > * {
      position: relative;
      z-index: 1;
    }

    /* Decorative mask that blends the pill into the card border */
    &::after {
      // Keep this below the content within the element so it doesn't obscure it
      z-index: 0;
      pointer-events: none;
      // Hide the bottom border so the pill looks like part of the card
      content: "";
      position: absolute;
      bottom: -1px;
      left: -1px;
      right: -1px;
      height: 15px;
      // background: white;
      border: 2px solid white;
      border-top: 0;
    }
  }

  &__status-stripe {
    position: absolute;
    top: -9px;
    right: 12px;
    z-index: 30;
  }

  &__post-type-tag {
    @include standardFont;
    display: inline-flex;
    align-items: center;
    padding: 0 4px 0 0;
    font-size: 12px;
    font-weight: 600;
    color: gray;
    // line-height: 1.2;
  }

  &__post-type-tag-icon {
    margin-right: 4px;
  }

  &__post-type-tag-text {
    display: inline-block;
  }

  &__post-snippet {
    flex-grow: 1;
  }

  &__post-snippet-transition-shell {
    overflow: hidden;
    transition: height 0.22s ease;
  }

  &__post-snippet-transition-item {
    width: 100%;
  }

  &__post-snippet-textarea {
    scroll-behavior: smooth;
  }

  &__used-mark {
    margin: 10px 12px 0;
  }

  &__divider {
    width: calc(100% - 24px);
    margin: 0 12px 18px;
    border-bottom: 1px rgba(0, 0, 0, 0.1) solid;
  }

  &__footer-divider {
    margin-bottom: 10px;
    border-bottom: 1px rgba(0, 0, 0, 0.1) solid;
  }

  &__debug-post-id-row {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 8px;
    margin-left: 5px;
    font-size: 11px;
    color: rgba(0, 0, 0, 0.65);
    flex-wrap: nowrap;
    min-width: 0;
  }

  &__debug-created {
    flex-shrink: 0;
    white-space: nowrap;
  }

  &__debug-post-id-copy {
    flex: 1;
    min-width: 0;

    :deep(.copy-text-snippet) {
      min-width: 0;
      max-width: 100%;
    }

    :deep(.copy-text-snippet__text-itself) {
      font-size: 10px;
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 100%;
    }
  }

  &__debug-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 700;
    margin-top: 4px;
    margin-left: 5px;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.65);
  }

  &__caption-structure-debug {
    margin-top: 8px;
    margin-left: 5px;
    margin-right: 5px;
  }

  &__caption-structure-debug-label {
    @include standardFont;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: rgba(0, 0, 0, 0.5);
    margin-bottom: 4px;
  }

  &__caption-structure-debug-body {
    @include standardFont;
  }
}

.post-preview-section-snippet-switch-enter-active,
.post-preview-section-snippet-switch-leave-active {
  transition:
    opacity 0.18s ease,
    transform 0.22s ease;
}

.post-preview-section-snippet-switch-enter-from,
.post-preview-section-snippet-switch-leave-to {
  opacity: 0;
  transform: translateY(4px);
}

@media (prefers-reduced-motion: reduce) {
  .post-preview-section {
    &__post-snippet-transition-shell {
      transition: none;
      height: auto !important;
    }
  }

  .post-preview-section-snippet-switch-enter-active,
  .post-preview-section-snippet-switch-leave-active {
    transition: none;
  }

  .post-preview-section-snippet-switch-enter-from,
  .post-preview-section-snippet-switch-leave-to {
    opacity: 1;
    transform: none;
  }
}
</style>
